import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../components/Header";
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

export default function SubmitVideo() {
  const [verse, setVerse] = useState("");
  const [title, setTitle] = useState("");
  const [video, setVideo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMsg, setStatusMsg] = useState("");
  const [claimId, setClaimId] = useState(null);
  const [description, setDescription] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!token) navigate("/login");
  }, [token, navigate]);

  useEffect(() => {
    // Read claimId and verse from URL params or location state
    const params = new URLSearchParams(location.search);
    const verseParam = params.get("verse") || location.state?.verse;
    const claimParam = params.get("claimId") || location.state?.claimId;
    if (verseParam) setVerse(verseParam);
    if (claimParam) setClaimId(claimParam);
  }, [location]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 500 * 1024 * 1024) {
      setStatusMsg("File too large. Maximum size is 500MB.");
      setVideo(null);
      return;
    }
    if (!file.type.startsWith("video/")) {
      setStatusMsg("Only video files are allowed.");
      setVideo(null);
      return;
    }
    setVideo(file);
    setStatusMsg("");
    setProgress(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!verse || !video) {
      setStatusMsg("Verse and video file are required.");
      return;
    }

    setUploading(true);
    setProgress(0);
    setStatusMsg("Initializing upload...");

    try {
      const CHUNK_SIZE = 5 * 1024 * 1024;
      const totalChunks = Math.ceil(video.size / CHUNK_SIZE);
      const initRes = await axios.post(
        `${API_URL}/videos/upload/init`,
        { verse, title: title || `VerSeer: ${verse}`, totalChunks, fileSize: video.size, fileType: video.type, description },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { uploadId } = initRes.data;

      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, video.size);
        const chunk = video.slice(start, end);
        const formData = new FormData();
        formData.append("chunk", chunk, "chunk");
        formData.append("uploadId", uploadId);
        formData.append("chunkIndex", i);
        formData.append("totalChunks", totalChunks);

        await axios.post(`${API_URL}/videos/upload/chunk`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        const percent = Math.floor(((i + 1) / totalChunks) * 100);
        setProgress(percent);
        setStatusMsg(`Uploading: ${percent}%`);
      }

      setStatusMsg("Finalizing...");
      const completeRes = await axios.post(`${API_URL}/videos/upload/complete`, { uploadId }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // ✅ If this video is linked to a claim, mark the claim as completed
      if (claimId && completeRes.data.video) {
        const videoId = completeRes.data.video.id;
        await axios.post(
          `${API_URL}/creator-claims/complete`,
          { claim_id: claimId, video_id: videoId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setStatusMsg("Upload complete and claim marked as completed!");
      } else {
        setStatusMsg("Upload complete! Video pending review.");
      }

      // Navigate to My Claims after a short delay
      setTimeout(() => navigate("/my-claims"), 1500);
    } catch (err) {
      console.error("Upload error:", err);
      setStatusMsg(err.response?.data?.error || "Upload failed. You can try resuming later.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="container mx-auto px-4 py-8 max-w-3xl">
        <h1 className="text-3xl font-bold text-primary mb-2">Submit Video Explanation</h1>
        <p className="text-gray-500 mb-6">Your video will be reviewed and published on VerSeer's YouTube channel.</p>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Verse Reference *</label>
            <input
              type="text"
              placeholder="e.g., John 3:16"
              value={verse}
              onChange={(e) => setVerse(e.target.value)}
              disabled={!!claimId} // 🔒 Lock if claiming from a request
              className={`w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                claimId ? "bg-gray-100 cursor-not-allowed" : ""
              }`}
              required
            />
            {claimId && (
              <p className="text-xs text-gray-500 mt-1">
                ⚡ Verse automatically set from your claim – it cannot be changed.
              </p>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Video Title (auto‑generated from verse if left blank)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Video Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Briefly describe what this video explains..."
            />
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-1">Video File (MP4, max 500MB) *</label>
            <input
              type="file"
              accept="video/*"
              onChange={handleFileChange}
              className="w-full"
              required
            />
          </div>

          {uploading && (
            <div className="mb-4">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div className="bg-primary h-2.5 rounded-full transition-all" style={{ width: `${progress}%` }}></div>
              </div>
              <div className="text-center text-sm text-gray-600 mt-1">{progress}%</div>
            </div>
          )}

          {statusMsg && (
            <div className={`mb-4 p-3 rounded-md text-center ${statusMsg.includes("complete") ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
              {statusMsg}
            </div>
          )}

          <button
            type="submit"
            disabled={uploading}
            className="w-full bg-primary text-white py-2 rounded-md font-semibold hover:bg-opacity-90 transition disabled:opacity-50"
          >
            {uploading ? "Uploading..." : "Submit for Review"}
          </button>
        </form>
      </main>
    </div>
  );
}